import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Users, UserPlus, Plus, Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import { residentPeopleAPI } from '../../lib/api';
import SmsNotifyControl from '../../components/notify/SmsNotifyControl';
import ActionHistoryPanel from '../../components/shared/ActionHistoryPanel';
import { actionHistoryAPI } from '../../lib/api';
import { TEXT } from './assistantText';

// Closed action registry mirrored from the server (property_people.ACTION_TYPES).
// Phase 6 (Call) is cancelled — messaging-only, so `call` is not offered at all.
const ACTION_TYPES = [
    { type: 'message', k: 'atMessage' },
    { type: 'deliveries', k: 'atDeliveries' },
    { type: 'maintenance', k: 'atMaintenance' },
    { type: 'parking', k: 'atParking' },
    { type: 'pickup', k: 'atPickup' },
    { type: 'concierge', k: 'atConcierge' },
    { type: 'im_away', k: 'atImAway' },
    { type: 'custom_instruction', k: 'atCustom' },
];
const triLabel = (k) => ({ en: TEXT.en[k], fr: TEXT.fr[k], es: TEXT.es[k] });
const actionName = (tx, type) => {
    const def = ACTION_TYPES.find((a) => a.type === type);
    return def ? tx[def.k] : type;
};
// Action types where a resident may add a free-text note shown to their visitors.
const NOTE_TYPES = new Set(['custom_instruction', 'deliveries']);
// The note is the resident's OWN words (single-language, per ruling). Read the
// current language first, else any value that was saved.
const noteOf = (a, lang) => { const o = a.auto_response_text || {}; return o[lang] || Object.values(o).find(Boolean) || ''; };

export const PropertyPeopleScreen = ({ tx, lang, token, onBack, surface }) => {
    const [me, setMe] = useState(null);
    const [people, setPeople] = useState([]);
    const [actions, setActions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddPerson, setShowAddPerson] = useState(false);
    const [showAddAction, setShowAddAction] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', phone: '' });
    const [actionType, setActionType] = useState('message');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [noteEditFor, setNoteEditFor] = useState(null);
    const [noteText, setNoteText] = useState('');

    const load = useCallback(async () => {
        try {
            const [m, p, a] = await Promise.all([
                residentPeopleAPI.me(token),
                residentPeopleAPI.listPeople(token),
                residentPeopleAPI.listActions(token),
            ]);
            setMe(m.data);
            setPeople(p.data.people || []);
            setActions(a.data.actions || []);
        } catch { /* transient */ } finally { setLoading(false); }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const isMaster = me?.tenant_role === 'master';

    const addPerson = async () => {
        if (!form.name.trim()) { setError(tx.ppNameReq); return; }
        setBusy(true); setError('');
        try {
            await residentPeopleAPI.addPerson(token, {
                name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null,
                person_type: 'tenant', tenant_role: 'additional',
            });
            setForm({ name: '', email: '', phone: '' });
            setShowAddPerson(false);
            await load();
        } catch (e) {
            const s = e?.response?.status;
            setError(s === 409 ? tx.ppCap : s === 403 ? tx.ppNotMaster : tx.ppErr);
        } finally { setBusy(false); }
    };

    const addAction = async () => {
        setBusy(true); setError('');
        try {
            await residentPeopleAPI.addAction(token, {
                type: actionType, label: triLabel(ACTION_TYPES.find((a) => a.type === actionType).k),
                enabled: true, order: actions.length,
            });
            setShowAddAction(false);
            setActionType('message');
            await load();
        } catch (e) {
            const s = e?.response?.status;
            setError(s === 409 ? tx.ppActionCap : tx.ppErr);
        } finally { setBusy(false); }
    };

    const toggleAction = async (a) => {
        setBusy(true); setError('');
        try { await residentPeopleAPI.patchAction(token, a.id, { enabled: !a.enabled }); await load(); }
        catch (e) { setError(e?.response?.status === 409 ? tx.ppActionCap : tx.ppErr); }
        finally { setBusy(false); }
    };

    const removeAction = async (aid) => {
        setBusy(true); setError('');
        try { await residentPeopleAPI.deleteAction(token, aid); await load(); }
        catch { setError(tx.ppErr); } finally { setBusy(false); }
    };

    const saveNote = async (a) => {
        setBusy(true); setError('');
        try {
            await residentPeopleAPI.patchAction(token, a.id, { auto_response_text: { [lang]: noteText.trim() } });
            setNoteEditFor(null); setNoteText('');
            await load();
        } catch { setError(tx.ppErr); } finally { setBusy(false); }
    };

    const saveSms = async (payload) => {
        setBusy(true); setError('');
        try {
            const r = await residentPeopleAPI.setSmsPrefs(token, payload);
            setMe((m) => ({ ...(m || {}), sms: r.data?.sms || m?.sms }));
        } catch { setError(tx.ppErr); } finally { setBusy(false); }
    };

    const inputCls = 'w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50';

    return (
        <div data-testid="pp-screen">
            <button type="button" onClick={onBack} data-testid="pp-back"
                className="mb-4 text-sm text-cyan-300 hover:text-cyan-200 flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" />{tx.changeRoom || 'Back'}
            </button>

            <div className="flex items-center gap-2.5 mb-1">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-400/5 border border-cyan-400/30 shadow-[0_0_16px_rgba(34,211,238,0.15)]">
                    <Users className="w-5 h-5 text-cyan-300" />
                </span>
                <h2 className="text-white font-semibold text-lg">{tx.ppTitle}</h2>
            </div>
            {me?.unit_number && (
                <p className="text-slate-400 text-sm mb-4" data-testid="pp-unit">{tx.ppUnit} {me.unit_number}</p>
            )}

            {error && <p className="text-rose-400 text-sm mb-3" data-testid="pp-error">{error}</p>}

            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>
            ) : (
                <>
                    {/* ── My notifications (permanent on/off + temporary pause) ── */}
                    {me?.sms && (
                        <div className="mb-4" data-testid="pp-sms-settings">
                            <SmsNotifyControl state={me.sms} onSave={saveSms} testid="pp-sms"
                                labels={{
                                    title: tx.smsTitle, sendTo: tx.smsSendTo, noPhone: tx.smsNoPhone,
                                    smsOn: tx.smsOn, smsOff: tx.smsOff, pauseTitle: tx.smsPauseTitle,
                                    pause2h: tx.smsPause2h, pauseTomorrow: tx.smsPauseTomorrow,
                                    pause2d: tx.smsPause2d, pausedUntil: tx.smsPausedUntil,
                                    resumeNow: tx.smsResumeNow,
                                }} />
                        </div>
                    )}
                    {/* ── My recent actions (shared platform panel) ── */}
                    <div className="mb-4">
                        <ActionHistoryPanel
                            testid="pp-action-history"
                            api={{
                                list: () => actionHistoryAPI.list({ residentToken: token }),
                                dismiss: (ids) => actionHistoryAPI.dismiss({ residentToken: token }, ids),
                                clear: () => actionHistoryAPI.clear({ residentToken: token }),
                            }}
                            labels={{
                                title: tx.ahTitle, empty: tx.ahEmpty, clearAll: tx.ahClearAll,
                                remove: tx.ahRemove, footnote: tx.ahFootnote,
                                action: { acknowledge: tx.ahAcknowledge, resolve: tx.ahResolve, cancel: tx.ahCancel },
                            }}
                        />
                    </div>
                    {/* ── People ── */}
                    <div className={`${surface} rounded-2xl p-4 mb-4 border border-cyan-500/10 shadow-[0_0_24px_rgba(34,211,238,0.05)]`}>
                        <p className="text-white font-medium mb-3">{tx.ppPeopleHdr}</p>
                        {people.length === 0 ? (
                            <p className="text-slate-400 text-sm" data-testid="pp-people-empty">{tx.ppEmptyPeople}</p>
                        ) : (
                            <div className="space-y-2">
                                {people.map((p) => (
                                    <div key={p.id} data-testid={`pp-person-row-${p.id}`}
                                        className="flex items-center justify-between bg-slate-900/60 rounded-xl px-3 py-2.5 border border-white/5 transition-all duration-200 hover:-translate-y-0.5 hover:border-cyan-500/25">
                                        <div className="min-w-0">
                                            <p className="text-white text-sm font-medium truncate">{p.name}</p>
                                            <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border ${p.tenant_role === 'master' ? 'bg-cyan-500/15 text-cyan-200 border-cyan-400/30' : 'bg-slate-700/40 text-slate-300 border-white/10'}`}>
                                                {p.tenant_role === 'master' && <ShieldCheck className="w-3 h-3" />}
                                                {p.tenant_role === 'master' ? tx.ppRoleMaster : tx.ppRoleAdditional}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Add person — MASTER ONLY. Rendered only when /resident/me says
                            tenant_role==='master'; the server also enforces it (real gate). */}
                        {isMaster && !showAddPerson && (
                            <button type="button" onClick={() => { setError(''); setShowAddPerson(true); }} data-testid="pp-add-person-btn"
                                className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 text-sm py-2.5 transition-colors">
                                <UserPlus className="w-4 h-4" />{tx.ppAdd}
                            </button>
                        )}
                        {isMaster && showAddPerson && (
                            <div className="mt-3 space-y-2" data-testid="pp-add-person-form">
                                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder={tx.ppName} className={inputCls} data-testid="pp-person-name" />
                                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder={tx.ppEmail} className={inputCls} data-testid="pp-person-email" />
                                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    placeholder={tx.ppPhone} className={inputCls} data-testid="pp-person-phone" />
                                <div className="flex gap-2">
                                    <button type="button" onClick={addPerson} disabled={busy} data-testid="pp-person-save"
                                        className="flex-1 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-slate-950 shadow-lg shadow-cyan-500/20 text-sm font-semibold py-2.5 disabled:opacity-60">
                                        {busy ? tx.ppSaving : tx.ppSave}
                                    </button>
                                    <button type="button" onClick={() => { setShowAddPerson(false); setError(''); }} data-testid="pp-person-cancel"
                                        className="flex-1 rounded-xl border border-white/10 text-slate-300 text-sm py-2.5 hover:bg-white/5">
                                        {tx.ppCancel}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── This resident's own visitor actions ── */}
                    <div className={`${surface} rounded-2xl p-4 border border-cyan-500/10 shadow-[0_0_24px_rgba(34,211,238,0.05)]`}>
                        <p className="text-white font-medium">{tx.ppActionsHdr}</p>
                        <p className="text-slate-400 text-xs mb-3">{tx.ppActionsSub}</p>
                        {actions.length === 0 ? (
                            <p className="text-slate-400 text-sm" data-testid="pp-actions-empty">{tx.ppEmptyActions}</p>
                        ) : (
                            <div className="space-y-2">
                                {actions.map((a) => (
                                    <div key={a.id} data-testid={`pp-action-row-${a.id}`}
                                        className="bg-slate-900/60 rounded-xl px-3 py-2.5 border border-white/5 transition-colors hover:border-cyan-500/20">
                                        <div className="flex items-center justify-between">
                                            <span className="text-white text-sm">{actionName(tx, a.type)}</span>
                                            <div className="flex items-center gap-1.5">
                                                <button type="button" onClick={() => toggleAction(a)} disabled={busy} data-testid={`pp-action-toggle-${a.id}`}
                                                    className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${a.enabled ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' : 'bg-slate-700/40 text-slate-400 border-white/10'}`}>
                                                    {a.enabled ? tx.ppOn : tx.ppOff}
                                                </button>
                                                <button type="button" onClick={() => removeAction(a.id)} disabled={busy} data-testid={`pp-action-delete-${a.id}`}
                                                    className="p-1.5 rounded-lg border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 transition-colors">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                        {NOTE_TYPES.has(a.type) && (
                                            noteEditFor === a.id ? (
                                                <div className="mt-2 space-y-2" data-testid={`pp-note-form-${a.id}`}>
                                                    <label className="text-slate-400 text-xs">{tx.ppNoteLabel}</label>
                                                    <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
                                                        placeholder={tx.ppNotePlaceholder} className={`${inputCls} resize-none`} data-testid={`pp-note-input-${a.id}`} />
                                                    <div className="flex gap-2">
                                                        <button type="button" onClick={() => saveNote(a)} disabled={busy} data-testid={`pp-note-save-${a.id}`}
                                                            className="flex-1 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-slate-950 shadow-lg shadow-cyan-500/20 text-sm font-semibold py-2 disabled:opacity-60">{busy ? tx.ppSaving : tx.ppSave}</button>
                                                        <button type="button" onClick={() => { setNoteEditFor(null); setNoteText(''); }} data-testid={`pp-note-cancel-${a.id}`}
                                                            className="flex-1 rounded-xl border border-white/10 text-slate-300 text-sm py-2 hover:bg-white/5">{tx.ppCancel}</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="mt-2 flex items-start justify-between gap-2">
                                                    <p className="text-slate-300 text-xs flex-1" data-testid={`pp-note-text-${a.id}`}>
                                                        {noteOf(a, lang) || <span className="text-slate-500">{tx.ppNoteLabel}</span>}
                                                    </p>
                                                    <button type="button" onClick={() => { setError(''); setNoteEditFor(a.id); setNoteText(noteOf(a, lang)); }} data-testid={`pp-note-edit-${a.id}`}
                                                        className="text-cyan-300 hover:text-cyan-200 text-xs shrink-0">
                                                        {noteOf(a, lang) ? tx.ppEditNote : tx.ppAddNote}
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {!showAddAction && (
                            <button type="button" onClick={() => { setError(''); setShowAddAction(true); }} data-testid="pp-add-action-btn"
                                className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 text-sm py-2.5 transition-colors">
                                <Plus className="w-4 h-4" />{tx.ppAddAction}
                            </button>
                        )}
                        {showAddAction && (
                            <div className="mt-3 space-y-2" data-testid="pp-add-action-form">
                                <label className="text-slate-400 text-xs">{tx.ppActionType}</label>
                                <select value={actionType} onChange={(e) => setActionType(e.target.value)}
                                    className={inputCls} data-testid="pp-action-type">
                                    {ACTION_TYPES.map((a) => (
                                        <option key={a.type} value={a.type}>{tx[a.k]}</option>
                                    ))}
                                </select>
                                <div className="flex gap-2">
                                    <button type="button" onClick={addAction} disabled={busy} data-testid="pp-action-save"
                                        className="flex-1 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-slate-950 shadow-lg shadow-cyan-500/20 text-sm font-semibold py-2.5 disabled:opacity-60">
                                        {busy ? tx.ppSaving : tx.ppSave}
                                    </button>
                                    <button type="button" onClick={() => { setShowAddAction(false); setError(''); }} data-testid="pp-action-cancel"
                                        className="flex-1 rounded-xl border border-white/10 text-slate-300 text-sm py-2.5 hover:bg-white/5">
                                        {tx.ppCancel}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
